import { Menu, Select } from "@theorgchartdev/storybook";
import { FieldSelectListProps } from "@/Utils/ComponentLib";
import { appstr } from "@coreapp/utils/appstr";
import { IsPositionChart } from "@coreapp/utils/IsPositionChart";
import FieldTypePill from "../FieldsFormulasConfiguration/FieldTypePill";
import {
  BuildSystemFieldOptions,
  BuildTextFieldsOptions,
  enumSystemFieldsMode,
  isPositionDocument
} from "@/Utils/DataLib";
import { enumObjClassNames, enumObjectTypes } from "@coreapp/constants";
import { cloneElement, isValidElement, useMemo, type ReactElement } from "react";

const DEFAULT_CLASSNAME = enumObjClassNames.box;

const getPillType = (fieldType: string): "employee" | "position" | null => {
  if (fieldType === enumObjectTypes.position) return "position";
  if (fieldType === enumObjectTypes.holder) return "employee";
  return null;
};

export type FieldValue = {
  fieldName: string;
  fieldClass: string;
  fieldType: string;
};

export type FieldSelectorProps = {
  chartConfig: any;
  onClick: (option: any, value: FieldValue) => void;
  fieldName?: string;
  fieldType?: string;
  useMenu?: boolean;
  containerProps?: any;
  filterFields?: string[];
  onlyFromType?: string;
  disabled?: boolean;
  includePills?: boolean;
  dataTestIdPrefix?: string;
};

const ParseOptionValue = (optionValue: string) => {
  let fieldData: { name: string; className: string };

  if (
    optionValue?.substring(0, 4) !== `${enumObjClassNames.box}-` &&
    optionValue?.substring(0, 9) !== `${enumObjClassNames.position}-`
  ) {
    fieldData = {
      name: optionValue,
      className: DEFAULT_CLASSNAME
    };
  } else {
    const splitIndex = optionValue.indexOf("-");
    fieldData = {
      name: optionValue.substring(splitIndex + 1),
      className: optionValue.substring(0, splitIndex)
    };
  }

  return fieldData;
};

const FieldSelector = (props: FieldSelectorProps) => {
  const { chartConfig, onClick, fieldName, fieldType, includePills = true } = props;

  const isPosition = chartConfig ? IsPositionChart(chartConfig) : isPositionDocument();

  const buildFieldOptions = () => {
    let fieldOptions;
    if (chartConfig) {
      const fields = isPosition
        ? {
            [enumObjectTypes.position]: chartConfig.positionFields,
            [enumObjectTypes.holder]: chartConfig.holderFields
          }
        : chartConfig.headers;

      if (!fields) return [];
      fieldOptions = BuildTextFieldsOptions(
        fields,
        cleanUpOnClick,
        {},
        enumSystemFieldsMode.filterable,
        null,
        []
      );
    } else {
      fieldOptions = BuildSystemFieldOptions(
        cleanUpOnClick,
        {},
        enumSystemFieldsMode.filterable,
        null,
        []
      );
    }

    if (props.filterFields) {
      if (fieldOptions?.length && fieldOptions?.[0]?.options) {
        fieldOptions.map((option: any) => {
          option.options = option.options.filter(
            (opt: any) => !props.filterFields?.includes(opt.value)
          );
          return option;
        });
      } else {
        fieldOptions = fieldOptions.filter(
          (option: any) => !props.filterFields?.includes(option.value)
        );
      }
    }

    if (props.onlyFromType && chartConfig && IsPositionChart(chartConfig)) {
      if (props.onlyFromType === enumObjectTypes.position) {
        fieldOptions =
          fieldOptions.find((option: any) => option.value === "positionFields")?.options ?? [];
      } else {
        fieldOptions =
          fieldOptions.find((option: any) => option.value === "employeeFields")?.options ?? [];
      }
    }

    return fieldOptions;
  };

  const cleanUpOnClick = (option: any) => {
    const { name, className } = ParseOptionValue(option.value || "");
    const fieldValue: FieldValue = {
      fieldName: name,
      fieldClass: className,
      fieldType:
        className === enumObjClassNames.position ? enumObjectTypes.position : enumObjectTypes.holder
    };

    return onClick(option, fieldValue);
  };

  const fieldOptions = buildFieldOptions();
  const isDisabled = props.disabled ?? fieldOptions.length === 0;

  const rawTrigger = props.containerProps?.trigger;
  const resolvedTrigger = isValidElement(rawTrigger)
    ? cloneElement(rawTrigger as ReactElement<{ disabled?: boolean }>, {
        disabled: isDisabled
      })
    : rawTrigger;

  const currentValue = useMemo(() => {
    if (!fieldName) return null;
    const fieldClassname =
      fieldType === enumObjectTypes.position ? enumObjClassNames.position : enumObjClassNames.box;

    return `${fieldClassname}-${fieldName}`;
  }, [fieldType, fieldName]);

  const Component = props.useMenu ? Menu : Select;
  const dataTestId = props.dataTestIdPrefix
    ? `${props.dataTestIdPrefix}-field-select`
    : "import-filter-field-select";

  return (
    <Component
      trigger={resolvedTrigger}
      dataTestId={dataTestId}
      selectSize={props.containerProps?.selectSize ?? "small"}
      disabled={isDisabled}
      {...props.containerProps}
      inputOnly={false}
      listProps={{
        ...FieldSelectListProps(),
        enableSearch: false,
        ...props.containerProps?.listProps
      }}
      popoverProps={{
        flipOptions: {
          mainAxis: false
        },
        ...props.containerProps?.popoverProps
      }}
      placeholder={appstr("Select field")}
      options={fieldOptions}
      value={currentValue}
      rightContent={
        isPosition &&
        includePills &&
        fieldName &&
        fieldType && <FieldTypePill type={getPillType(fieldType)} pillProps={{ size: "compact" }} />
      }
    />
  );
};

export default FieldSelector;
